import React, { useState } from 'react';
import { Mail, Phone } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const TeamMemberCard = ({ member }) => {
  const [isFlipped, setIsFlipped] = useState(false);

  return (
    <div 
      className="relative h-[500px] cursor-pointer perspective-1000"
      onMouseEnter={() => setIsFlipped(true)}
      onMouseLeave={() => setIsFlipped(false)}
    >
      {/* Card Container with Flip Effect */}
      <div 
        className={`relative w-full h-full transition-transform duration-700 transform-style-3d ${
          isFlipped ? 'rotate-y-180' : ''
        }`}
      >
        {/* RECTO - Front Side (Image + Name) */}
        <div className="absolute inset-0 backface-hidden">
          <div className="bg-white rounded-2xl overflow-hidden shadow-xl h-full">
            {/* Image */}
            <div className="relative h-[400px] overflow-hidden bg-gray-200">
              <img
                src={member.image}
                alt={member.name}
                className="w-full h-full object-cover"
              />
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              
              {/* Name on Image */}
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <h3 className="text-3xl font-bold mb-2">
                  {member.name}
                </h3>
                <p className="text-orange-400 font-semibold text-2xl">
                  {member.title}
                </p>
              </div>
            </div>
            
            {/* Hover Indicator */}
            <div className="p-6 text-center">
              <p className="text-gray-500 text-base italic">
                Survolez pour en savoir plus
              </p>
            </div>
          </div>
        </div>

        {/* VERSO - Back Side (Information) */}
        <div className="absolute inset-0 backface-hidden rotate-y-180">
          <div 
            className="bg-gradient-to-br from-purple-900 to-orange-600 rounded-2xl overflow-hidden shadow-xl h-full p-8 text-white flex flex-col justify-between"
          >
            {/* Header */}
            <div>
              <h3 className="text-3xl font-bold mb-3">
                {member.name}
              </h3>
              <p className="text-orange-200 font-semibold text-2xl mb-6">
                {member.title}
              </p>
            </div>
            
            {/* Description */}
            <div className="flex-1 flex items-center">
              <p className="text-white/90 leading-relaxed text-2xl">
                {member.description}
              </p>
            </div>
            
            {/* Contact Info */}
            {(member.email || member.phone) && (
              <div className="space-y-3 border-t border-white/20 pt-6">
                {member.email && (
                  <a 
                    href={`mailto:${member.email}`}
                    className="flex items-center gap-3 text-white/90 hover:text-white transition-colors group/link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                      <Mail size={18} />
                    </div>
                    <span className="group-hover/link:underline">{member.email}</span>
                  </a>
                )}
                {member.phone && (
                  <a 
                    href={`tel:${member.phone}`}
                    className="flex items-center gap-3 text-white/90 hover:text-white transition-colors group/link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                      <Phone size={18} />
                    </div>
                    <span className="group-hover/link:underline">{member.phone}</span>
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const TeamSection = () => {
  const { language } = useLanguage();

  const teamMembers = [
    {
      id: 1,
      name: 'Elie KITULU MASINGA',
      title: language === 'fr' ? 'Directeur Gérant' : 'Managing Director',
      description: language === 'fr' 
        ? 'Ingénieur de formation, Elie dirige l\'entreprise et assure le développement stratégique du groupe. Plus de 16 ans d\'expérience dans la construction et les infrastructures.'
        : 'Engineer by training, Elie leads the company and ensures the strategic development of the group. Over 16 years of experience in construction and infrastructure.',
      image: 'https://customer-assets-wrfwihn1.emergentagent.net/job_construction-global/artifacts/bcirilka_IMG_5718.jpg',
      email: '',
      phone: ''
    },
    {
      id: 2,
      name: 'Thierry MASHAWU MUJINGA',
      title: language === 'fr' ? 'Directeur Général Adjoint' : 'Deputy General Director',
      description: language === 'fr'
        ? 'Expert en gestion de projets d\'infrastructures, Thierry coordonne l\'ensemble des opérations et assure la qualité de nos réalisations. Plus de 16 ans d\'expérience dans la construction et les infrastructures.'
        : 'Expert in infrastructure project management, Thierry coordinates all operations and ensures the quality of our achievements. Over 16 years of experience in construction and infrastructure.',
      image: 'https://customer-assets-wrfwihn1.emergentagent.net/job_construction-global/artifacts/8r8hbq2t_IMG_6255.jpg',
      email: '',
      phone: ''
    },
    {
      id: 3,
      name: 'Yannick KULA',
      title: language === 'fr' ? 'Responsable Technique' : 'Technical Manager',
      description: language === 'fr'
        ? 'Architecte et ingénieur civil, Yannick supervise tous les aspects techniques de nos projets de construction et s\'assure du respect des normes. Plus de 12 ans d\'expérience.'
        : 'Architect and civil engineer, Yannick oversees all technical aspects of our construction projects and ensures compliance with standards. Over 12 years of experience.',
      image: 'https://customer-assets-wrfwihn1.emergentagent.net/job_construction-global/artifacts/nv2bw2yw_IMG_6263.jpg',
      email: '',
      phone: ''
    },
    {
      id: 4,
      name: 'Bénédicte TABI',
      title: language === 'fr' ? 'Chargée de Communication' : 'Communication Officer',
      description: language === 'fr'
        ? 'Chargée de communication chez GCI, Bénédicte pilote la stratégie de marque, les relations presse et la communication digitale. Plus de 12 ans d\'expérience dans la communication d\'entreprise.'
        : 'GCI Communication Officer, Bénédicte drives brand strategy, press relations and digital communication. Over 12 years of experience in corporate communication.',
      image: 'https://customer-assets-wrfwihn1.emergentagent.net/job_construction-global/artifacts/bisj5oyp_2e81f210-984a-4859-a08a-75c41ad68003.JPG',
      email: '',
      phone: ''
    },
    {
      id: 5,
      name: 'Coralie NKOTE',
      title: language === 'fr' ? 'Responsable Ressources Humaines' : 'Human Resources Manager',
      description: language === 'fr'
        ? 'Responsable Ressources Humaines chez GCI, Coralie gère le recrutement, la formation et le bien-être des collaborateurs. Plus de 12 ans d\'expérience dans les ressources humaines.'
        : 'Human Resources Manager at GCI, Coralie handles recruitment, training and employee well-being. Over 12 years of experience in human resources.',
      image: 'https://customer-assets-wrfwihn1.emergentagent.net/job_construction-global/artifacts/tue9b723_IMG_6303.jpg',
      email: '',
      phone: ''
    },
    {
      id: 6,
      name: 'Nehemie KOPELE',
      title: language === 'fr' ? 'Chargé de Logistique' : 'Logistics Officer',
      description: language === 'fr'
        ? 'Chargé de Logistique chez GCI, Nehemie coordonne les flux de matériaux, la gestion des stocks et la logistique de chantier. Plus de 12 ans d\'expérience dans la logistique du bâtiment.'
        : 'GCI Logistics Officer, Nehemie coordinates material flows, stock management and site logistics. Over 12 years of experience in construction logistics.',
      image: 'https://customer-assets-wrfwihn1.emergentagent.net/job_construction-global/artifacts/ad3lfkib_CB83532B-D17E-4542-AC64-B9E9F11EC3DB.PNG',
      email: '',
      phone: ''
    }
  ];

  return (
    <section className="py-12 bg-gray-50 relative overflow-hidden">
      <div className="max-w-[1800px] mx-auto px-6 relative z-10">
        <div className="text-center mb-10">
          <h2 className="text-6xl font-bold mb-4" style={{ color: '#702963' }}>
            {language === 'fr' ? 'Notre Équipe' : 'Our Team'}
          </h2>
          <p className="text-2xl text-gray-600 max-w-3xl mx-auto">
            {language === 'fr'
              ? 'Des professionnels passionnés et expérimentés, unis par une vision commune : l\'excellence dans chaque projet.'
              : 'Passionate and experienced professionals, united by a common vision: excellence in every project.'}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-6">
          {teamMembers.map((member) => (
            <TeamMemberCard key={member.id} member={member} />
          ))}
        </div>
        <div className="text-center">
          <p className="text-2xl text-gray-600">
            {language === 'fr'
              ? 'Le groupe GLOBAL CONSTRUCTION & INGEGNERIE, ce sont plusieurs équipes spécialisées qui collaborent à vos côtés. Client ou partenaire, nous vous proposons des compétences adaptées à chaque étape, pour des projets de construction, rénovation ou installation photovoltaïque.'
              : 'The GLOBAL CONSTRUCTION & ENGINEERING group consists of several specialized teams working alongside you. Whether client or partner, we offer skills adapted to each stage, for construction, renovation or photovoltaic installation projects.'}
          </p>
        </div>
      </div>
    </section>
  );
};

export default TeamSection;
